import React, {
  useState,
  useEffect,
  useCallback,
  useId,
  useImperativeHandle,
  forwardRef,
  useMemo,
  useRef,
} from 'react';
import {
  getPatientDocuments,
  getPatientDocumentUrl,
  updatePatientDocument,
  updateIntakeDocument,
  deletePatientDocument,
  deletePatientIntakeDocument,
  getIntakes,
  getReferralDocs,
} from '../../services/api';
import { useStore } from '../../store';
import PDFViewerNative from '../PDFViewerNative';
import HistoricalClaimHistory from './HistoricalClaimHistory';
import type {
  PatientDocumentResponse,
  IntakeDocumentResponse,
  TaskListResponse,
} from '../../services/api/types.gen';
import { useToast } from '../../hooks/useToastContext';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@roger/ui-components';
import { formatDateToMMDDYYYY } from '../../utils/dateUtils';

interface IntakeDocumentWithMeta extends IntakeDocumentResponse {
  intakeId: string;
}

// Human-readable label for the `task_type` polymorphic discriminator
// (values: "visit", "order", "patient_communication", "misc_task",
// "document_upload", "task"). An unmapped value falls back to the raw string.
const TASK_TYPE_LABELS: Record<string, string> = {
  visit: 'Visit',
  order: 'Order',
  patient_communication: 'Communication',
  misc_task: 'Misc task',
  document_upload: 'Document upload',
  task: 'Task',
};

const formatTaskTag = (
  doc: PatientDocumentResponse,
  /** True when the document's task is known to belong to another episode. */
  isOtherEpisode: boolean,
  /** The date each of the patient's tasks displays. */
  taskDates: ReadonlyMap<string, string>
): string | null => {
  if (!doc.task_id) return null;
  // The task's *type*, deliberately, where the scope dropdown names the task
  // ("Visit" here against "Recert" there): the dropdown is picking between
  // tasks, while this only has to say what kind of thing the document came off.
  const typeLabel = doc.task_type
    ? (TASK_TYPE_LABELS[doc.task_type] ?? doc.task_type)
    : 'Task';
  // Prefer the date the task itself displays. The document response carries the
  // task's scheduled date, but a visit or order displays its service date
  // (visit_date / order_date), and the two diverge once a visit is charted on a
  // different day. Falling back to the response only for a task nothing knows
  // about keeps the date stable whichever episode is selected.
  const date = taskDates.get(doc.task_id) ?? doc.task_scheduled_date;
  const formatted = date ? formatDateToMMDDYYYY(date) : '';
  const dated = formatted ? `${typeLabel} ${formatted}` : typeLabel;
  // Only claimed when the task map actually places the task in another episode.
  // A task the map does not know about gets no marker, because nothing can be
  // said about where it belongs.
  return isOtherEpisode ? `[${dated} · Other episode]` : `[${dated}]`;
};

const LoadingSpinner: React.FC<{ size?: 'sm' | 'md'; text?: string }> = ({
  size = 'md',
  text,
}) => (
  <div className='flex items-center gap-2' role='status'>
    <div
      className={`animate-spin rounded-full border-2 border-primary border-t-transparent ${size === 'sm' ? 'h-4 w-4' : 'h-8 w-8'}`}
    />
    {text && <span className='text-sm text-muted-foreground'>{text}</span>}
  </div>
);

export interface PatientDocumentsTabHandle {
  refreshDocuments: () => void;
}

const EPISODE_SCOPE = '__episode__';
const ALL_EPISODES_SCOPE = '__all__';

/**
 * Which slice of the patient's documents the list shows. `task` also decides
 * where an upload lands; the two episode-wide scopes both upload patient-level.
 */
type DocumentScope =
  { kind: 'episode' } | { kind: 'all' } | { kind: 'task'; taskId: string };

const scopeToValue = (scope: DocumentScope): string => {
  if (scope.kind === 'task') return scope.taskId;
  return scope.kind === 'episode' ? EPISODE_SCOPE : ALL_EPISODES_SCOPE;
};

/** Null for anything that is not a sentinel or a task currently on offer. */
const valueToScope = (
  value: string,
  tasks: TaskListResponse[]
): DocumentScope | null => {
  if (value === EPISODE_SCOPE) return { kind: 'episode' };
  if (value === ALL_EPISODES_SCOPE) return { kind: 'all' };
  return tasks.some(t => t.id === value)
    ? { kind: 'task', taskId: value }
    : null;
};

export interface DocumentEpisodeScope {
  /** The episode on screen; null when the patient has none. */
  episodeId: string | null;
  /**
   * task id -> episode id over every one of the patient's live tasks. Empty when
   * the loaded tasks do not describe this patient, which makes every document
   * fall open rather than be attributed to the wrong episode.
   */
  taskEpisodeIds: ReadonlyMap<string, string>;
  /**
   * task id -> the date that task displays, over the same tasks. Separate from
   * the document's own `task_scheduled_date`, which is the scheduled date and so
   * disagrees with a visit charted on a different day.
   */
  taskDates: ReadonlyMap<string, string>;
  /**
   * False when the map could not be built (the task fetch failed), as opposed to
   * built and empty. The filter fails open either way, so this is what stops the
   * tab claiming an episode-filtered list it is not actually showing.
   *
   * Note for producers: false means the tasks are not known at all, so the
   * caller has no episode task list to offer either — the tab's copy assumes
   * `documentContextTasks` is empty whenever this is false.
   */
  taskEpisodeIdsKnown: boolean;
  /**
   * False until both the episode and the task fetch have settled for this
   * patient, either way. The tab shows nothing while it is false, so it must
   * become true even when a fetch fails.
   */
  ready: boolean;
}

interface PatientDocumentsTabProps {
  patientId: string;
  /** Patient name for the upload toast target (e.g. "David Moore"). */
  patientLabel?: string;
  /** Visits, orders, and other tasks that support attachments (exclude communications). */
  documentContextTasks?: TaskListResponse[];
  /** Episode context for the "This Episode" scope. */
  episodeScope: DocumentEpisodeScope;
}

const NO_TASKS: TaskListResponse[] = [];

/**
 * How long to withhold the list while the episode context settles. The API
 * client sets no request timeout, so without this a request that hangs rather
 * than fails would hide every task-attached document indefinitely — worse than
 * showing an unscoped list, which is what expiring falls through to.
 */
export const SCOPE_HOLD_TIMEOUT_MS = 10_000;

/** One label for both the Select option and the trigger's accessible name. */
const taskScopeLabel = (task: TaskListResponse): string =>
  `${task.display_name || 'Task'} - ${formatDateToMMDDYYYY(task.task_date ?? task.scheduled_date) || 'N/A'}`;

const PatientDocumentsTab = forwardRef<
  PatientDocumentsTabHandle,
  PatientDocumentsTabProps
>((props, ref) => {
  const {
    patientId,
    patientLabel,
    documentContextTasks = NO_TASKS,
    episodeScope,
  } = props;
  const {
    episodeId,
    taskEpisodeIds,
    taskDates,
    taskEpisodeIdsKnown,
    ready: scopeReady,
  } = episodeScope;

  const { success: showSuccess, error: showError } = useToast();

  // Background uploads live in the store so the persistent toast + refresh
  // survive this tab (and its PDF viewer) unmounting when the user navigates.
  const startTaskDocumentUpload = useStore(
    state => state.startTaskDocumentUpload
  );
  const startPatientDocumentUpload = useStore(
    state => state.startPatientDocumentUpload
  );

  const sortedContextTasks = useMemo(() => {
    return [...documentContextTasks].sort(
      (a, b) =>
        new Date(b.task_date ?? b.scheduled_date).getTime() -
        new Date(a.task_date ?? a.scheduled_date).getTime()
    );
  }, [documentContextTasks]);

  /** null = the user has not chosen; the effective scope below is derived. */
  const [scope, setScope] = useState<DocumentScope | null>(null);

  /**
   * Whether "This Episode" can be honoured at all. It needs an episode AND a
   * usable task map: with either missing, the predicate matches nothing and the
   * scope would sit selected over a completely unfiltered list, which reads as a
   * filtered one. Better to fall back to All Episodes and say why.
   */
  const episodeScopeAvailable = episodeId != null && taskEpisodeIdsKnown;

  // A task scope is honoured whatever the episode context is, for as long as the
  // task is still on offer: with no episode on screen the tab is patient-wide,
  // and picking a task there must still filter the list and still route the
  // upload to that task. A task that has left the list (the caller switched
  // episode) is dropped here rather than in an effect, so no frame is painted
  // filtered on the previous episode's task.
  //
  // Memoized so the branches, which are object literals, keep one identity
  // across renders where the scope, the episode and the task list are all
  // unchanged; the document memos below take it as a dependency.
  const effectiveScope: DocumentScope = useMemo(() => {
    if (
      scope?.kind === 'task' &&
      sortedContextTasks.some(t => t.id === scope.taskId)
    ) {
      return scope;
    }
    const chosen = scope?.kind === 'task' ? null : scope;
    return episodeScopeAvailable
      ? (chosen ?? { kind: 'episode' })
      : { kind: 'all' };
  }, [scope, sortedContextTasks, episodeScopeAvailable]);

  /** The task uploads attach to, and the task the list filters on. */
  const scopeTaskId =
    effectiveScope.kind === 'task' ? effectiveScope.taskId : null;

  const [patientDocuments, setPatientDocuments] = useState<
    PatientDocumentResponse[]
  >([]);
  const [intakeDocuments, setIntakeDocuments] = useState<
    IntakeDocumentWithMeta[]
  >([]);
  const [patientDocsLoading, setPatientDocsLoading] = useState(false);
  const [intakeDocsLoading, setIntakeDocsLoading] = useState(false);
  const [activeDocumentId, setActiveDocumentId] = useState<string>('');
  const [documentUrls, setDocumentUrls] = useState<Record<string, string>>({});
  // ── Fetch patient documents ──────────────────────────────────────

  const fetchPatientDocuments = useCallback(async () => {
    setPatientDocsLoading(true);
    try {
      const response = await getPatientDocuments({
        path: { patient_id: patientId },
        query: { include_task_documents: true },
      });
      if (response?.data) {
        // Exclude intake-type docs — those are already shown via the intake relationship
        const docs = response.data.filter(d => d.document_type !== 'intake');
        setPatientDocuments(docs);
        const urlMap: Record<string, string> = {};
        // TODO: If this list grows large, switch to lazy URL signing (on access)
        // and add pagination so we do not eagerly fetch every document URL upfront.
        for (const doc of docs) {
          try {
            const urlResponse = await getPatientDocumentUrl({
              path: { patient_id: patientId, document_id: doc.id },
            });
            const urlData = urlResponse?.data as { url?: string } | undefined;
            if (urlData?.url) {
              urlMap[doc.id] = urlData.url;
            }
          } catch (err) {
            console.error(`Error fetching URL for document ${doc.id}:`, err);
            showError(
              'Document Error',
              'There was an error fetching the document.'
            );
          }
        }
        setDocumentUrls(prev => ({ ...prev, ...urlMap }));
      }
    } catch (err) {
      console.error('Failed to load patient documents:', err);
    } finally {
      setPatientDocsLoading(false);
    }
  }, [patientId]);

  useImperativeHandle(ref, () => ({
    refreshDocuments: fetchPatientDocuments,
  }));

  useEffect(() => {
    fetchPatientDocuments();
  }, [fetchPatientDocuments]);

  // ── Refresh after a background upload completes ──────────────────
  // The upload lives in the store (so it survives this tab unmounting); when it
  // succeeds it bumps a per-patient / per-task tick. Re-fetch on a bump so the
  // new document and count appear — including when the user navigated away and
  // came back mid-upload. The scoped task tick covers task uploads that bump only
  // the task tick (no patientId). Switching the scope dropdown doesn't *need* a
  // refetch — filteredDocuments re-filters the already-loaded set client-side —
  // though switching to/from a task whose tick already bumped this session still
  // fires one harmless idempotent refetch (its observed tick value changes).
  //
  // Ticks are normalized to 0 (their absent value) and the refs seed at 0 so the
  // very first upload (0 → 1) triggers a refetch. Seeding at undefined would swallow
  // that first bump, leaving the list stale until a second upload. Mirror of the
  // patientUploadTick watcher in PatientDetail.tsx.
  const patientUploadTick = useStore(
    state => state.patientUploadTick[patientId] ?? 0
  );
  const scopedTaskUploadTick = useStore(state =>
    scopeTaskId ? (state.taskUploadTick[scopeTaskId] ?? 0) : 0
  );
  const prevPatientTickRef = useRef(0);
  const prevTaskTickRef = useRef(0);
  useEffect(() => {
    const patientBumped = patientUploadTick !== prevPatientTickRef.current;
    const taskBumped = scopedTaskUploadTick !== prevTaskTickRef.current;
    if (patientBumped || taskBumped) {
      fetchPatientDocuments();
    }
    prevPatientTickRef.current = patientUploadTick;
    prevTaskTickRef.current = scopedTaskUploadTick;
  }, [patientUploadTick, scopedTaskUploadTick, fetchPatientDocuments]);

  // ── Fetch intake documents ───────────────────────────────────────

  useEffect(() => {
    setIntakeDocsLoading(true);
    getIntakes({
      query: { patient_id: patientId },
    })
      .then(async response => {
        if (response?.data?.items) {
          const allIntakeDocs: IntakeDocumentWithMeta[] = [];
          const urlMap: Record<string, string> = {};

          for (const intake of response.data.items) {
            if (intake.documents && intake.documents.length > 0) {
              // Include intakeId for URL refresh to work correctly
              const docsWithMeta = intake.documents.map(doc => ({
                ...doc,
                intakeId: intake.id,
              }));
              allIntakeDocs.push(...docsWithMeta);

              try {
                const docsResponse = await getReferralDocs({
                  path: { intake_id: intake.id },
                });
                if (docsResponse?.data) {
                  const docsData = docsResponse.data as {
                    documents?: Array<{ id: string; url: string }>;
                  };
                  if (docsData.documents) {
                    for (const docUrl of docsData.documents) {
                      urlMap[docUrl.id] = docUrl.url;
                    }
                  }
                }
              } catch (err) {
                console.error(
                  `Error fetching URLs for intake ${intake.id}:`,
                  err
                );
              }
            }
          }

          setIntakeDocuments(allIntakeDocs);
          setDocumentUrls(prev => ({ ...prev, ...urlMap }));
        }
      })
      .catch(err => console.error('Failed to load intake documents:', err))
      .finally(() => setIntakeDocsLoading(false));
  }, [patientId]);

  // Clear a task scope the episode on screen no longer contains. The switch is
  // already handled above, where `effectiveScope` ignores a task that has left
  // the list; this only discards the dead state so it cannot come back if the
  // same task reappears. Either way it works only because
  // `documentContextTasks` is itself episode-scoped by the caller — were it ever
  // made patient-wide, the old episode's task would still be in this list and
  // the scope would silently stick.
  useEffect(() => {
    if (
      scope?.kind === 'task' &&
      !sortedContextTasks.some(t => t.id === scope.taskId)
    ) {
      setScope(null);
    }
  }, [scope, sortedContextTasks]);

  /**
   * True when the document's task is known to sit outside the episode on screen.
   * A task the map has never heard of is NOT treated as another episode's: the
   * map only covers live, episode-attached tasks that have finished loading, so
   * "unknown" means "cannot say". Since "This Episode" is the default view,
   * guessing "other episode" there would make a live clinical document
   * unreachable unless the user knew to switch scopes.
   */
  const isOutsideEpisode = useCallback(
    (doc: PatientDocumentResponse) => {
      if (!doc.task_id || episodeId == null) return false;
      const docEpisodeId = taskEpisodeIds.get(doc.task_id);
      return docEpisodeId !== undefined && docEpisodeId !== episodeId;
    },
    [episodeId, taskEpisodeIds]
  );

  // Hold the list back until the episode context can answer, rather than
  // painting every episode's documents and then dropping rows once it lands —
  // that would pull the document the user just opened out from under them, via
  // the selection-repair effect below. Not conditioned on the effective scope:
  // while the episode is still in flight the scope itself reads as "all", so
  // gating on the scope would let exactly the wrong paint through.
  const [holdExpired, setHoldExpired] = useState(false);
  useEffect(() => {
    // The reset arm is defensive only: `ready` is monotonic within a mount,
    // because the markers behind it are cleared only on a patient change, which
    // also re-keys this component.
    if (scopeReady) {
      setHoldExpired(false);
      return;
    }
    const timer = setTimeout(() => setHoldExpired(true), SCOPE_HOLD_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [scopeReady]);

  // Giving up the wait hands the user an unscoped list; record that as their
  // scope so a late-arriving episode context cannot narrow it back under them,
  // which is the same yank the hold exists to prevent, just deferred. The
  // control reappears with the context, so narrowing becomes their choice.
  useEffect(() => {
    if (holdExpired) setScope(prev => prev ?? { kind: 'all' });
  }, [holdExpired]);

  const scopePending = !scopeReady && !holdExpired;

  const filteredPatientDocuments = useMemo(() => {
    // A document with no task has no episode to be reattributed to, so — like
    // the intake documents below — it is in whichever episode-wide scope
    // resolves, and the hold has nothing to protect it from. (A task scope can
    // never be the one that resolves out of a hold: the control is not rendered
    // while pending, so nothing can have chosen one.)
    if (scopePending) return patientDocuments.filter(d => !d.task_id);
    if (effectiveScope.kind === 'all') return patientDocuments;
    if (effectiveScope.kind === 'task') {
      return patientDocuments.filter(d => d.task_id === effectiveScope.taskId);
    }
    return patientDocuments.filter(d => !isOutsideEpisode(d));
  }, [patientDocuments, effectiveScope, isOutsideEpisode, scopePending]);

  // Intake documents hang off the intake's admission, not an episode, so there
  // is no episode to attribute them to: they stay visible in both episode-wide
  // scopes and are hidden only when the list is narrowed to one task. Which also
  // means the hold has nothing to protect them from — whichever episode-wide
  // scope resolves, they belong in it — so they are not withheld, and a slow
  // task fetch cannot put the referral packet out of reach.
  const displayedIntakeDocuments = useMemo(() => {
    if (effectiveScope.kind === 'task') return [];
    return intakeDocuments;
  }, [intakeDocuments, effectiveScope]);

  // "All Episodes" is always an option; the other two sources are conditional.
  // A control holding one already-selected option can change nothing, so it is
  // not rendered — which also covers the whole hold, where neither source has
  // arrived yet, so the control cannot appear and then vanish once it turns out
  // the patient has no episode.
  const scopeOptionCount =
    1 + (episodeScopeAvailable ? 1 : 0) + sortedContextTasks.length;
  // The pending term is defensive and untested: while the scope is settling
  // neither an episode nor a task list is on hand, so the count is 1 regardless.
  // Covering it would mean constructing a state the producer cannot emit.
  const scopeSelectorVisible = !scopePending && scopeOptionCount > 1;

  /**
   * The caption follows the selector, so no copy describes a control that is not
   * on screen, and is suppressed while the scope settles: the list is empty
   * there, so every branch would be a claim about nothing. The spinner carries
   * that window.
   */
  // Not tied to the selector: with an episode on screen but no usable task map
  // there is nothing to choose and so no control, and that is exactly when the
  // caption has to explain why the list is not episode-filtered.
  const captionVisible =
    !scopePending && (scopeSelectorVisible || episodeId != null);
  const captionId = useId();
  const labelId = useId();
  const valueId = useId();

  /**
   * How many of the patient's documents are known to sit on another episode, and
   * so are being held back from this list. Said in the caption so a short list
   * is explained rather than left to guess at. Skipped while pending purely to
   * avoid the walk; the caption is suppressed there anyway.
   */
  const otherEpisodeDocumentCount = useMemo(
    () =>
      scopePending
        ? 0
        : patientDocuments.filter(d => isOutsideEpisode(d)).length,
    [patientDocuments, isOutsideEpisode, scopePending]
  );

  const scopeCaption = (() => {
    if (effectiveScope.kind === 'task') {
      return 'Showing documents attached to this task. Uploads add files to this task.';
    }
    // A document has no episode of its own — only the task it hangs off has one
    // — so an upload under either episode-wide scope lands on the patient and
    // shows up in every episode. Said plainly, because "This Episode" otherwise
    // implies the opposite.
    const uploadClause =
      sortedContextTasks.length > 0
        ? ' Uploads are added to the patient; select a task to upload only to that task.'
        : ' Uploads are added to the patient, so they appear in every episode.';
    if (effectiveScope.kind === 'all') {
      // An episode on screen with no usable map means the visits either failed or
      // are taking too long to keep waiting for — different claims, and only the
      // fetch having settled tells them apart.
      if (episodeId != null && !taskEpisodeIdsKnown) {
        return scopeReady
          ? `Visit details could not be loaded, so every document for this patient is shown.${uploadClause}`
          : `Visit details are still loading, so every document for this patient is shown.${uploadClause}`;
      }
      return `Every document for this patient, across all episodes, including attachments on tasks.${uploadClause}`;
    }
    // No gate on the documents having loaded: the count is drawn from
    // `patientDocuments`, so it is 0 until they land and the clause is absent
    // for that reason alone.
    const otherClause =
      otherEpisodeDocumentCount > 0
        ? ` ${otherEpisodeDocumentCount} more in other episodes.`
        : '';
    return `Documents for this episode, plus intake documents and anything not tied to a visit.${uploadClause}${otherClause}`;
  })();

  // ── Keep selection valid when scope or data changes ──────────────
  // Only re-points when the current selection has gone, so a document opened
  // during the hold stays open once the hold lifts: the pending list is a subset
  // of whichever scope resolves, so the id is still valid. That makes which
  // document opens first depend on which fetch won, which is accepted — holding
  // the choice still beats moving it under the user.

  useEffect(() => {
    const allDocs = [...displayedIntakeDocuments, ...filteredPatientDocuments];
    if (allDocs.length === 0) {
      setActiveDocumentId('');
      return;
    }
    if (!activeDocumentId || !allDocs.some(d => d.id === activeDocumentId)) {
      setActiveDocumentId(allDocs[0].id);
    }
  }, [displayedIntakeDocuments, filteredPatientDocuments, activeDocumentId]);

  // ── Handlers ─────────────────────────────────────────────────────

  const handleDocumentRename = async (documentId: string, fileName: string) => {
    const isPatientDoc = patientDocuments.some(d => d.id === documentId);

    try {
      if (isPatientDoc) {
        await updatePatientDocument({
          path: { patient_id: patientId, document_id: documentId },
          body: { file_name: fileName },
          throwOnError: true,
        });
        setPatientDocuments(prev =>
          prev.map(doc =>
            doc.id === documentId ? { ...doc, file_name: fileName } : doc
          )
        );
      } else {
        await updateIntakeDocument({
          path: { document_id: documentId },
          body: { file_name: fileName },
          throwOnError: true,
        });
        setIntakeDocuments(prev =>
          prev.map(doc =>
            doc.id === documentId ? { ...doc, file_name: fileName } : doc
          )
        );
      }
      showSuccess('Document Renamed', 'Document renamed successfully.');
    } catch (err) {
      console.error('Error renaming document:', err);
      showError('Rename Failed', 'Failed to rename document.');
    }
  };

  const handleDocumentAdd = async (file: File, displayName: string) => {
    // Hand the upload to the background store: the persistent toast and the
    // list/count refresh (via the tick effect below) survive this tab and its
    // PDF viewer unmounting when the user navigates away mid-upload. The viewer
    // shows an optimistic "Uploading…" preview meanwhile; on success we return
    // the persisted doc so the viewer swaps the temp doc for it by id AND keeps
    // it focused. On failure/cancel the store returns null, so we throw and the
    // viewer drops its stale preview — the uploader's toast carries the message.
    const patientTarget = patientLabel ?? 'this patient';

    // Routed by the scope id itself rather than by the lookup succeeding, so a
    // task-scoped upload cannot be silently downgraded to a patient upload. The
    // lookup only supplies the label, and cannot miss: a task scope survives
    // `effectiveScope` only while its task is still in this same list.
    let doc: PatientDocumentResponse | null;
    if (scopeTaskId) {
      const scopedTask = sortedContextTasks.find(t => t.id === scopeTaskId);
      const label = `${scopedTask?.display_name || 'Task'} · ${patientTarget}`;
      doc = await startTaskDocumentUpload({
        taskId: scopeTaskId,
        patientId,
        file,
        displayName,
        targetLabel: label,
      });
    } else {
      doc = await startPatientDocumentUpload({
        patientId,
        file,
        displayName,
        targetLabel: patientTarget,
      });
    }

    if (!doc) {
      throw new Error('upload failed or canceled');
    }
    return {
      id: doc.id,
      fileName: doc.file_name,
      mimeType: doc.mime_type ?? undefined,
    };
  };

  const handleDocumentDelete = async (documentId: string): Promise<void> => {
    const isPatientDoc = patientDocuments.some(d => d.id === documentId);
    try {
      if (isPatientDoc) {
        await deletePatientDocument({
          path: { patient_id: patientId, document_id: documentId },
          throwOnError: true,
        });
        setPatientDocuments(prev => prev.filter(d => d.id !== documentId));
      } else {
        await deletePatientIntakeDocument({
          path: { patient_id: patientId, document_id: documentId },
          throwOnError: true,
        });
        setIntakeDocuments(prev => prev.filter(d => d.id !== documentId));
      }
      if (activeDocumentId === documentId) {
        setActiveDocumentId('');
      }
      setDocumentUrls(prev => {
        const next = { ...prev };
        delete next[documentId];
        return next;
      });
      showSuccess('Document Deleted', 'Document deleted successfully.');
    } catch (err) {
      console.error('Error deleting document:', err);
      showError(
        'Delete Failed',
        'Failed to delete document. Please try again.'
      );
      throw err;
    }
  };

  const handleRefreshDocumentUrl = useCallback(
    async (documentId: string): Promise<string | null> => {
      // Check if this is an intake document
      const intakeDoc = intakeDocuments.find(d => d.id === documentId);

      try {
        if (intakeDoc) {
          // Intake document - use getReferralDocs API
          const docsResponse = await getReferralDocs({
            path: { intake_id: intakeDoc.intakeId },
          });
          const docsData = docsResponse.data as {
            documents?: Array<{ id: string; url: string }>;
          };
          if (docsData?.documents) {
            const doc = docsData.documents.find(d => d.id === documentId);
            if (doc?.url) {
              setDocumentUrls(prev => ({ ...prev, [documentId]: doc.url }));
              return doc.url;
            }
          }
          return null;
        } else {
          // Patient document - use getPatientDocumentUrl API
          const urlResponse = await getPatientDocumentUrl({
            path: { patient_id: patientId, document_id: documentId },
          });
          const urlData = urlResponse?.data as { url?: string } | undefined;
          if (urlData?.url) {
            setDocumentUrls(prev => ({ ...prev, [documentId]: urlData.url! }));
            return urlData.url;
          }
          return null;
        }
      } catch (err) {
        console.error(`Error refreshing URL for document ${documentId}:`, err);
        return null;
      }
    },
    [patientId, intakeDocuments]
  );

  // ── Render ───────────────────────────────────────────────────────

  return (
    <>
      <HistoricalClaimHistory patientId={patientId} />
      <div className='overflow-hidden rounded-lg border bg-card'>
        <div className='flex flex-col gap-2 px-4 py-3 border-b bg-muted/30'>
          {/* min-h-7 matches the sm trigger, so the row keeps its height whether it
            holds the spinner, the control, or (for a patient who never gets a
            control) nothing. A reservation that collapses once loading finishes
            would only move the shift later. */}
          <div className='flex min-h-7 flex-wrap items-center gap-2'>
            {/* Rendered only when it has more than one option, which is exactly
              when something is being filtered out: with a single option the
              effective scope is always "all", so nothing is hidden and the
              control could change nothing. An episode with no tasks does reach
              it (This Episode / All Episodes), which is where the only route to
              another episode's documents used to disappear; an episode with an
              unusable task map does not, and the caption carries the
              explanation there instead. The per-task items come from
              `documentContextTasks`, which excludes communications because they
              take no attachments here, while the episode predicate above reads a
              map of ALL the patient's tasks — so a document attached to a
              communication in this episode is not orphaned by the narrower list. */}
            {scopeSelectorVisible ? (
              <Select
                value={scopeToValue(effectiveScope)}
                onValueChange={v => {
                  // Null only for a value that names neither sentinel nor a task
                  // on offer, which no menu this component renders can produce.
                  // Ignoring it leaves the scope where it was.
                  const next = valueToScope(v, sortedContextTasks);
                  if (next) setScope(next);
                }}
              >
                <SelectTrigger
                  selectSize='sm'
                  className='w-[min(100%,360px)]'
                  // role="combobox" takes no name from its contents, so the name
                  // is assembled explicitly: the hidden label first, so it opens
                  // with something stable rather than moving under voice control
                  // every time the scope changes, then the value, so what is
                  // selected is still reported.
                  aria-labelledby={`${labelId} ${valueId}`}
                  // Unconditional: the caption's own condition is implied by the
                  // trigger being rendered at all.
                  aria-describedby={captionId}
                >
                  <span id={labelId} className='sr-only'>
                    Document scope
                  </span>
                  <span id={valueId}>
                    <SelectValue />
                  </span>
                </SelectTrigger>
                <SelectContent>
                  {episodeScopeAvailable && (
                    <SelectItem value={EPISODE_SCOPE}>This Episode</SelectItem>
                  )}
                  <SelectItem value={ALL_EPISODES_SCOPE}>
                    All Episodes
                  </SelectItem>
                  {sortedContextTasks.map(task => (
                    <SelectItem key={task.id} value={task.id}>
                      {taskScopeLabel(task)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : null}
            {(intakeDocsLoading || patientDocsLoading || scopePending) && (
              <LoadingSpinner size='sm' text='Loading documents...' />
            )}
          </div>
          {captionVisible ? (
            <p id={captionId} className='text-xs text-muted-foreground'>
              {scopeCaption}
            </p>
          ) : null}
        </div>
        <div>
          <div className='h-[calc(100vh-12rem)]'>
            <PDFViewerNative
              documents={[
                ...displayedIntakeDocuments.map(doc => ({
                  id: doc.id,
                  fileName: doc.file_name,
                  mimeType: doc.mime_type ?? null,
                  tooltipText: doc.task_type_tag
                    ? `[Intake · ${doc.task_type_tag}] ${doc.file_name}`
                    : `[Intake] ${doc.file_name}`,
                  isPersisted: true,
                  usedInExtraction: doc.used_in_extraction ?? true,
                  canDelete: true,
                })),
                ...filteredPatientDocuments.map(doc => {
                  const taskTag = formatTaskTag(
                    doc,
                    isOutsideEpisode(doc),
                    taskDates
                  );
                  return {
                    id: doc.id,
                    fileName: doc.file_name,
                    mimeType: doc.mime_type ?? null,
                    tooltipText: taskTag
                      ? `${taskTag} ${doc.file_name}`
                      : undefined,
                    isPersisted: true,
                    usedInExtraction: true,
                    canDelete: !doc.is_signed,
                  };
                }),
              ]}
              documentUrls={documentUrls}
              activeDocumentId={activeDocumentId}
              onActiveDocumentChange={setActiveDocumentId}
              onDocumentRename={handleDocumentRename}
              onDocumentAdd={handleDocumentAdd}
              onDocumentDelete={handleDocumentDelete}
              onRefreshUrl={handleRefreshDocumentUrl}
            />
          </div>
        </div>
      </div>
    </>
  );
});

PatientDocumentsTab.displayName = 'PatientDocumentsTab';

export default PatientDocumentsTab;
